import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiDownload, FiEdit2, FiEye, FiSearch, FiExternalLink } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const Underwriting = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");
  const [isProofOpen, setIsProofOpen] = useState(false);
  const [proofData, setProofData] = useState(null);
  const [isRefreshingProof, setIsRefreshingProof] = useState(false);
  const [refreshProofError, setRefreshProofError] = useState("");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusForm, setStatusForm] = useState({
    status_id: "",
    remarks: "",
    bank_reference_number: "",
  });
  const [activePurchaseId, setActivePurchaseId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [exportError, setExportError] = useState("");
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isBatchPaymentOpen, setIsBatchPaymentOpen] = useState(false);
  const [batchFile, setBatchFile] = useState(null);
  const [batchFileName, setBatchFileName] = useState("");
  const [batchError, setBatchError] = useState("");
  const [batchSuccess, setBatchSuccess] = useState("");
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [isBatchSuccessOpen, setIsBatchSuccessOpen] = useState(false);
  const [isBatchEnrollmentOpen, setIsBatchEnrollmentOpen] = useState(false);
  const [enrollmentFile, setEnrollmentFile] = useState(null);
  const [enrollmentFileName, setEnrollmentFileName] = useState("");
  const [enrollmentError, setEnrollmentError] = useState("");
  const [enrollmentSuccess, setEnrollmentSuccess] = useState("");
  const [isEnrollmentSubmitting, setIsEnrollmentSubmitting] = useState(false);
  const [isEnrollmentSuccessOpen, setIsEnrollmentSuccessOpen] = useState(false);
  const [isBatchUtilizationOpen, setIsBatchUtilizationOpen] = useState(false);
  const [utilizationFile, setUtilizationFile] = useState(null);
  const [utilizationFileName, setUtilizationFileName] = useState("");
  const [utilizationError, setUtilizationError] = useState("");
  const [utilizationSuccess, setUtilizationSuccess] = useState("");
  const [isUtilizationSubmitting, setIsUtilizationSubmitting] = useState(false);
  const [isUtilizationSuccessOpen, setIsUtilizationSuccessOpen] = useState(false);
  const navigate = useNavigate();

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          return;
        }

        const response = await api.get("/product-purchase-statuses", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          return;
        }

        setStatusOptions(response.data?.data || []);
      } catch (error) {
        return;
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    const fetchUnderwriting = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/underwriting", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: query,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
            payment_date_from: paymentDateFrom || undefined,
            payment_date_to: paymentDateTo || undefined,
            purchase_status_id: purchaseStatusFilter || undefined,
            payment_type_id: paymentTypeFilter || undefined,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load underwriting data.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load underwriting data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnderwriting();
  }, [query, sortBy, sortDir, page, perPage, paymentDateFrom, paymentDateTo, purchaseStatusFilter, paymentTypeFilter, refreshKey]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleView = async (id) => {
    setIsViewOpen(true);
    setViewError("");
    setViewData(null);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setViewError("Missing access token.");
        return;
      }

      const response = await api.get(`/underwriting/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setViewError("Failed to load details.");
        return;
      }

      setViewData(response.data?.data || null);
    } catch (error) {
      setViewError("Failed to load details.");
    }
  };

  const handleViewProof = (row) => {
    setProofData(row);
    setIsProofOpen(true);
  };

  const handleOpenStatus = (row) => {
    if ([2, 5, 6].includes(Number(row.status_id))) {
      return;
    }
    setActivePurchaseId(row.id);
    setStatusForm({
      status_id: row.status_id ? String(row.status_id) : "",
      remarks: row.remarks || "",
      bank_reference_number: row.bank_reference_number || "",
    });
    setSaveError("");
    setSaveSuccess("");
    setIsStatusOpen(true);
  };

  const handleUpdateStatus = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      if (Number(statusForm.status_id) === 2 && !statusForm.bank_reference_number.trim()) {
        setSaveError("Bank reference number is required to approve.");
        return;
      }

      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSaveError("Missing access token.");
        return;
      }

      const response = await api.patch(
        `/underwriting/${activePurchaseId}/status`,
        {
          status_id: Number(statusForm.status_id),
          remarks: statusForm.remarks || null,
          bank_reference_number: statusForm.bank_reference_number || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to update status.";
        if (message && typeof message === "object") {
          setSaveError(Object.values(message).flat().join(" "));
        } else {
          setSaveError(message);
        }
        return;
      }

      setSaveSuccess(response.data?.message || "Status updated.");
      setIsStatusOpen(false);
      setPage(1);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setSaveError("Failed to update status.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportBatchPayment = async () => {
    if (!paymentDateFrom || !paymentDateTo) {
      setExportError("Payment Date From and Payment Date To are required for export.");
      return;
    }

    setExportError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setExportError("Missing access token.");
        return;
      }

      const response = await api.get("/underwriting/export", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: query,
          sort_by: sortBy,
          sort_dir: sortDir,
          payment_date_from: paymentDateFrom,
          payment_date_to: paymentDateTo,
          purchase_status_id: purchaseStatusFilter || undefined,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `underwriting-export-${paymentDateFrom}-to-${paymentDateTo}.csv`;
      link.href = url;
      link.download = match?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError("Failed to export underwriting data.");
    }
  };

  const handleExportEnrollment = async () => {
    if (!paymentDateFrom || !paymentDateTo) {
      setExportError("Payment Date From and Payment Date To are required for export.");
      return;
    }

    setExportError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setExportError("Missing access token.");
        return;
      }

      const response = await api.get("/underwriting/export-enrollment", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: query,
          sort_by: sortBy,
          sort_dir: sortDir,
          payment_date_from: paymentDateFrom,
          payment_date_to: paymentDateTo,
          purchase_status_id: purchaseStatusFilter || undefined,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `underwriting-enrollment-${paymentDateFrom}-to-${paymentDateTo}.csv`;
      link.href = url;
      link.download = match?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError("Failed to export enrollment data.");
    }
  };

  const handleDownloadTemplate = (type) => {
    const templates = {
      payment: [
        "purchase_id",
        "referral_code",
        "product_code",
        "payment_date",
        "bank_reference_number",
      ],
      enrollment: ["purchase_id", "referral_code", "amaphil_id"],
      utilization: ["purchase_id", "referral_code", "consumed_at"],
    };
    const headers = templates[type] || [];
    if (headers.length === 0) {
      return;
    }

    const csvContent = `${headers.join(",")}\n`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    let filename = "batch-template.csv";
    if (type === "payment") {
      filename = "batch-payment-template.csv";
    } else if (type === "enrollment") {
      filename = "batch-enrollment-amaphil-id-template.csv";
    } else if (type === "utilization") {
      filename = "batch-utilization-template.csv";
    }
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleBatchPaymentSubmit = async (event) => {
    event.preventDefault();
    if (!batchFile) {
      setBatchError("Please upload a CSV file.");
      return;
    }

    setBatchError("");
    setBatchSuccess("");
    setIsBatchSubmitting(true);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setBatchError("Missing access token.");
        return;
      }

      const formData = new FormData();
      formData.append("file", batchFile);

      const response = await api.post("/underwriting/batch-payments", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status !== "success") {
        setBatchError(response.data?.message || "Failed to upload batch payment.");
        return;
      }

      setBatchSuccess(response.data?.message || "Batch payment queued.");
      setBatchFile(null);
      setBatchFileName("");
      setIsBatchPaymentOpen(false);
      setIsBatchSuccessOpen(true);
    } catch (error) {
      setBatchError("Failed to upload batch payment.");
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const handleBatchEnrollmentSubmit = async (event) => {
    event.preventDefault();
    if (!enrollmentFile) {
      setEnrollmentError("Please upload a CSV file.");
      return;
    }

    setEnrollmentError("");
    setEnrollmentSuccess("");
    setIsEnrollmentSubmitting(true);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setEnrollmentError("Missing access token.");
        return;
      }

      const formData = new FormData();
      formData.append("file", enrollmentFile);

      const response = await api.post(
        "/underwriting/batch-enrollment-amaphil-id",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.status !== "success") {
        setEnrollmentError(
          response.data?.message || "Failed to upload batch enrollment."
        );
        return;
      }

      setEnrollmentSuccess(response.data?.message || "Batch enrollment queued.");
      setEnrollmentFile(null);
      setEnrollmentFileName("");
      setIsBatchEnrollmentOpen(false);
      setIsEnrollmentSuccessOpen(true);
    } catch (error) {
      setEnrollmentError("Failed to upload batch enrollment.");
    } finally {
      setIsEnrollmentSubmitting(false);
    }
  };

  const handleBatchUtilizationSubmit = async (event) => {
    event.preventDefault();
    if (!utilizationFile) {
      setUtilizationError("Please upload a CSV file.");
      return;
    }

    setUtilizationError("");
    setUtilizationSuccess("");
    setIsUtilizationSubmitting(true);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setUtilizationError("Missing access token.");
        return;
      }

      const formData = new FormData();
      formData.append("file", utilizationFile);

      const response = await api.post("/underwriting/batch-utilizations", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status !== "success") {
        setUtilizationError(
          response.data?.message || "Failed to upload batch utilization."
        );
        return;
      }

      setUtilizationSuccess(response.data?.message || "Batch utilization queued.");
      setUtilizationFile(null);
      setUtilizationFileName("");
      setIsBatchUtilizationOpen(false);
      setIsUtilizationSuccessOpen(true);
    } catch (error) {
      setUtilizationError("Failed to upload batch utilization.");
    } finally {
      setIsUtilizationSubmitting(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return "₱ 0.00";
    }
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("₱", "₱ ");
  };

  const getAccountStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "pre registered") {
      return "bg-orange-100 text-orange-700";
    }
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "for activation") {
      return "bg-blue-100 text-blue-700";
    }
    if (normalized === "active") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "inactive") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "expired") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const getPurchaseStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "approved") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "rejected") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const isUrlExpired = (expiresAt) => {
    if (!expiresAt) {
      return true;
    }
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) {
      return true;
    }
    return expiry.getTime() <= Date.now();
  };

  const handleRefreshProofLink = async (purchaseId) => {
    if (!purchaseId) {
      return;
    }
    setIsRefreshingProof(true);
    setRefreshProofError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setRefreshProofError("Missing access token.");
        return;
      }

      const response = await api.post(
        `/underwriting/${purchaseId}/refresh-proof-url`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status !== "success") {
        setRefreshProofError("Failed to refresh proof link.");
        return;
      }

      const refreshed = response.data?.data || {};
      setRows((prev) =>
        prev.map((row) =>
          row.id === purchaseId
            ? {
                ...row,
                signed_url: refreshed.signed_url || row.signed_url,
                url_expires_at: refreshed.url_expires_at || row.url_expires_at,
              }
            : row
        )
      );

      if (proofData && proofData.id === purchaseId) {
        setProofData((prev) => ({
          ...prev,
          signed_url: refreshed.signed_url || prev?.signed_url,
          url_expires_at: refreshed.url_expires_at || prev?.url_expires_at,
        }));
      }
    } catch (error) {
      setRefreshProofError("Failed to refresh proof link.");
    } finally {
      setIsRefreshingProof(false);
    }
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Underwriting
        </p>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">
              Underwriting Review
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Monitor member purchases, account status, and validation progress.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {[
                {
                  label: "Batch Payment History",
                  path: "/batch-payment-history",
                },
                {
                  label: "Batch Enrollment History",
                  path: "/batch-enrollment-amaphil-id-history",
                },
                {
                  label: "Batch Utilizations History",
                  path: "/batch-utilizations-history",
                },
                {
                  label: "Batch Payout History",
                  path: "/batch-payout-history",
                },
              ].map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    <FiExternalLink className="text-[14px]" />
                  </span>
                  <span>{`Go to ${item.label}`}</span>
                </button>
              ))}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsExportOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                <FiDownload />
                Export Options
              </button>
              {isExportOpen && (
                <div className="absolute right-0 top-full z-10 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setIsExportOpen(false);
                      handleExportBatchPayment();
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  >
                    Export for Batch Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsExportOpen(false);
                      handleExportEnrollment();
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  >
                    Export for Batch Enrollment Amaphil ID
                  </button>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsBatchOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Batch Upload
              </button>
              {isBatchOpen && (
                <div className="absolute right-0 top-full z-10 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setIsBatchOpen(false);
                      setBatchError("");
                      setBatchSuccess("");
                      setIsBatchPaymentOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                  >
                    Upload Batch Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsBatchOpen(false);
                      setEnrollmentError("");
                      setEnrollmentSuccess("");
                      setIsBatchEnrollmentOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                  >
                    Upload Batch Enrollment Amaphil ID
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsBatchOpen(false);
                      setUtilizationError("");
                      setUtilizationSuccess("");
                      setIsBatchUtilizationOpen(true);
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100"
                  >
                    Upload Batch Utilizations
                  </button>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsTemplateOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                <FiDownload />
                Download Template
              </button>
              {isTemplateOpen && (
                <div className="absolute right-0 top-full z-10 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setIsTemplateOpen(false);
                      handleDownloadTemplate("payment");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  >
                    Download Template for Batch Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsTemplateOpen(false);
                      handleDownloadTemplate("enrollment");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  >
                    Download Template for Batch Enrollment Amaphil ID
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsTemplateOpen(false);
                      handleDownloadTemplate("utilization");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 hover:text-slate-800"
                  >
                    Download Template for Batch Utilizations
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </form>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>Display</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <select
            value={purchaseStatusFilter}
            onChange={(event) => {
              setPurchaseStatusFilter(event.target.value);
              setPage(1);
            }}
            className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">All Purchase Status</option>
            {statusOptions.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          <select
            value={paymentTypeFilter}
            onChange={(event) => {
              setPaymentTypeFilter(event.target.value);
              setPage(1);
            }}
            className="min-w-[180px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">All Payment Types</option>
            <option value="1">Individual</option>
            <option value="2">Group</option>
          </select>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Payment Date From</span>
              <input
                type="date"
                value={paymentDateFrom}
                onChange={(event) => {
                  setPaymentDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Payment Date To</span>
              <input
                type="date"
                value={paymentDateTo}
                onChange={(event) => {
                  setPaymentDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:ml-auto" />
        </div>

      {exportError && (
        <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {exportError}
        </div>
      )}
      {refreshProofError && (
        <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {refreshProofError}
        </div>
      )}

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
              <tr>
                {[
                  { key: "referral_code", label: "Referral Code" },
                  { key: "member_name", label: "Member Name" },
                  { key: "account_status", label: "User Account Status" },
                    { key: "payment_date", label: "Payment Date" },
                    { key: "payment_type", label: "Payment Type" },
                    { key: "group_name", label: "Group Name" },
                    { key: "purchase_status", label: "Purchase Product Status" },
                    { key: "amount", label: "Paid Amount" },
                    { key: "amaphil_id", label: "Amaphil ID" },
                    { key: "proof", label: "Proof of Payment" },
                ].map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="cursor-pointer px-4 py-3 text-xs font-semibold"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortBy === column.key && (
                        <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={9}>
                    Loading underwriting records...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={9}>
                    No underwriting records found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const fullName = [
                    row.first_name,
                    row.middle_name,
                    row.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {row.referral_code || "-"}
                      </td>
                      <td className="px-4 py-3">{fullName || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${getAccountStatusClass(
                            row.account_status_name
                          )}`}
                        >
                          {row.account_status_name || "Pre Registered"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {formatDateTime(row.payment_date)}
                      </td>
                      <td className="px-4 py-3">
                        {row.payment_type_name || "-"}
                      </td>
                      <td className="px-4 py-3">{row.group_name || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${getPurchaseStatusClass(
                            row.purchase_status_name
                          )}`}
                        >
                          {row.purchase_status_name || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {formatAmount(row.amount)}
                      </td>
                      <td className="px-4 py-3">{row.amaphil_id || "-"}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleViewProof(row)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={!row.signed_url}
                        >
                          <FiEye />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRefreshProofLink(row.id)}
                          disabled={!row.upload_id || isRefreshingProof || !isUrlExpired(row.url_expires_at)}
                          className="ml-2 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isRefreshingProof ? "Refreshing..." : "Refresh"}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleView(row.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FiEye />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenStatus(row)}
                            disabled={[2, 5, 6].includes(Number(row.status_id))}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <FiEdit2 />
                            Change Status
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isViewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60">
          <div className="h-full w-full overflow-y-auto bg-white p-6 shadow-2xl shadow-slate-900/30 sm:p-10">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Underwriting
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Member & Purchase Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review account and purchase details for this member.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {viewError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {viewError}
              </div>
            )}

            {!viewError && !viewData && (
              <p className="mt-6 text-sm text-slate-400">Loading details...</p>
            )}

            {viewData && (
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {[
                  { label: "Referral Code", value: viewData.referral_code },
                  { label: "Amaphil ID", value: viewData.amaphil_id },
                  {
                    label: "Member Name",
                    value: [
                      viewData.first_name,
                      viewData.middle_name,
                      viewData.last_name,
                      viewData.suffix,
                    ]
                      .filter(Boolean)
                      .join(" "),
                  },
                  { label: "Email", value: viewData.email },
                  { label: "Mobile Number", value: viewData.mobile_number },
                  { label: "User Account Status", value: viewData.account_status_name },
                  { label: "Payment Date", value: formatDateTime(viewData.payment_date) },
                  { label: "Payment Type", value: viewData.payment_type_name },
                  { label: "Group Name", value: viewData.group_name },
                  { label: "Purchase Status", value: viewData.purchase_status_name },
                  { label: "Paid Amount", value: formatAmount(viewData.amount) },
                  { label: "Product", value: viewData.product_title },
                  { label: "Product Tagline", value: viewData.product_secondary_title },
                  { label: "Remarks", value: viewData.remarks },
                  { label: "Proof File", value: viewData.original_file_name },
                  { label: "Proof Type", value: viewData.file_type },
                  { label: "Proof Size", value: viewData.file_size },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                  >
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      {item.value || "-"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
              {viewData?.signed_url && (
                <button
                  type="button"
                  onClick={() => window.open(viewData.signed_url, "_blank")}
                  className="ml-3 rounded-2xl border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  View Proof
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {isStatusOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Purchase Status
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Change Status
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Update the purchase status and add remarks if needed.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsStatusOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {saveError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {saveError}
              </div>
            )}
            {saveSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {saveSuccess}
              </div>
            )}

            <form onSubmit={handleUpdateStatus} className="mt-6 space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Purchase Status
                </label>
                <select
                  value={statusForm.status_id}
                  onChange={(event) =>
                    setStatusForm((prev) => ({
                      ...prev,
                      status_id: event.target.value,
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  required
                >
                  <option value="">Select status</option>
                  {statusOptions.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Remarks
                </label>
                <textarea
                  rows={4}
                  value={statusForm.remarks}
                  onChange={(event) =>
                    setStatusForm((prev) => ({
                      ...prev,
                      remarks: event.target.value,
                    }))
                  }
                  placeholder="Optional remarks"
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              {Number(statusForm.status_id) === 2 && (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Bank Reference Number
                  </label>
                  <input
                    type="text"
                    value={statusForm.bank_reference_number}
                    onChange={(event) =>
                      setStatusForm((prev) => ({
                        ...prev,
                        bank_reference_number: event.target.value,
                      }))
                    }
                    placeholder="Required for approved status"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    required
                  />
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsStatusOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isSaving ? "Saving..." : "Save Status"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isProofOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Proof of Payment
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Payment Attachment
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsProofOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {proofData?.original_file_name ? (
                <p>{proofData.original_file_name}</p>
              ) : (
                <p>No file available.</p>
              )}
            </div>

            {proofData?.signed_url && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => window.open(proofData.signed_url, "_blank")}
                  className="inline-flex items-center justify-center rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  View Proof
                </button>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsProofOpen(false)}
                className="rounded-2xl border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isBatchPaymentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Batch Upload
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Upload Batch Payment
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Upload a CSV with Purchase ID, Referral Code, Product Code, Payment Date, and Bank Reference Number.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchPaymentOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {batchError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {batchError}
              </div>
            )}
            {batchSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {batchSuccess}
              </div>
            )}

            <form onSubmit={handleBatchPaymentSubmit} className="mt-6 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                <p className="font-semibold text-slate-700">
                  {batchFileName || "Choose CSV file"}
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="mt-3 block w-full text-sm text-slate-500"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setBatchFile(file || null);
                    setBatchFileName(file?.name || "");
                  }}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsBatchPaymentOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isBatchSubmitting}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isBatchSubmitting ? "Uploading..." : "Upload CSV"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isBatchEnrollmentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Batch Upload
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Upload Batch Enrollment Amaphil ID
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Upload a CSV with Purchase ID, Referral Code, and Amaphil ID.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchEnrollmentOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {enrollmentError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {enrollmentError}
              </div>
            )}
            {enrollmentSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {enrollmentSuccess}
              </div>
            )}

            <form onSubmit={handleBatchEnrollmentSubmit} className="mt-6 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                <p className="font-semibold text-slate-700">
                  {enrollmentFileName || "Choose CSV file"}
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="mt-3 block w-full text-sm text-slate-500"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setEnrollmentFile(file || null);
                    setEnrollmentFileName(file?.name || "");
                  }}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsBatchEnrollmentOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEnrollmentSubmitting}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isEnrollmentSubmitting ? "Uploading..." : "Upload CSV"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isBatchUtilizationOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Batch Upload
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Upload Batch Utilizations
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Upload a CSV with Purchase ID, Referral Code, and Consumed Date (YYYY-MM-DD).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchUtilizationOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {utilizationError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {utilizationError}
              </div>
            )}
            {utilizationSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {utilizationSuccess}
              </div>
            )}

            <form onSubmit={handleBatchUtilizationSubmit} className="mt-6 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                <p className="font-semibold text-slate-700">
                  {utilizationFileName || "Choose CSV file"}
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="mt-3 block w-full text-sm text-slate-500"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setUtilizationFile(file || null);
                    setUtilizationFileName(file?.name || "");
                  }}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsBatchUtilizationOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUtilizationSubmitting}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                >
                  {isUtilizationSubmitting ? "Uploading..." : "Upload CSV"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isBatchSuccessOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Batch Payment Uploaded
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your batch payment upload is queued. You can visit Batch Payment
                  History to check progress and review any failed rows.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchSuccessOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsBatchSuccessOpen(false)}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsBatchSuccessOpen(false);
                  navigate("/batch-payment-history");
                }}
                className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Go to Batch Payment History
              </button>
            </div>
          </div>
        </div>
      )}

      {isEnrollmentSuccessOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Batch Enrollment Uploaded
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your batch enrollment upload is queued. You can visit Batch
                  Enrollment History to check progress and review any failed rows.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsEnrollmentSuccessOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsEnrollmentSuccessOpen(false)}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEnrollmentSuccessOpen(false);
                  navigate("/batch-enrollment-amaphil-id-history");
                }}
                className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Go to Batch Enrollment History
              </button>
            </div>
          </div>
        </div>
      )}

      {isUtilizationSuccessOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Batch Utilizations Uploaded
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your batch utilization upload is queued. You can visit Batch
                  Utilizations History to check progress and review any failed rows.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsUtilizationSuccessOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsUtilizationSuccessOpen(false)}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsUtilizationSuccessOpen(false);
                  navigate("/batch-utilizations-history");
                }}
                className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Go to Batch Utilizations History
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Underwriting;
